/* eslint-disable */
import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import $ from 'jquery';
import moment from 'moment';

import Master from '../Master';
import IziToast from '../IziToast';
import { DestroyTable, DrawTable } from '../../utils/Functions';

// Cap at 2dp — bare toLocaleString() with no options lets binary
// floating-point noise from upstream arithmetic leak straight into the UI.
const moneyOpts = { maximumFractionDigits: 2 };

const SuspenseAccount = ({ user }) => {
  const userToken = user ? user.token : {};
  const requestConfig = {
    headers: { Authorization: `Bearer ${userToken}`, 'Content-Type': 'application/json' },
  };

  // Filters
  const [filterDate, setFilterDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterParticular, setFilterParticular] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  // Raw records as returned by the API. The visible `records` (further down)
  // is derived from this list + the filters above, so filters apply LIVE
  // (no "Search" round-trip) and the backend stays simple.
  const [allRecords, setAllRecords] = useState([]);
  // Reconcile modal
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [reconcilePropertyId, setReconcilePropertyId] = useState('');
  const [reconcileTenantId, setReconcileTenantId] = useState('');
  const [reconcileUnitId, setReconcileUnitId] = useState('');
  const [reconcileParticular, setReconcileParticular] = useState('rent');
  const [reconcileReason, setReconcileReason] = useState('');
  const [isReconciling, setIsReconciling] = useState(false);

  const propertyRef = useRef(null);
  const tenantRef = useRef(null);

  useEffect(() => {
    loadRecords();
    loadProperties();
  }, []);

  useEffect(() => {
    if (reconcilePropertyId) loadTenantsForProperty(reconcilePropertyId);
  }, [reconcilePropertyId]);

  const loadProperties = async () => {
    try {
      let url;
      if (user.type === 'admin') {
        url = `${process.env.REACT_APP_API_URL}properties?where={"status":"active"}&take=1000&order={"name":"ASC"}`;
      } else {
        url = `${process.env.REACT_APP_API_URL}properties/byuser?status=active&user_id=${user.id}`;
      }
      const { data: { response } } = await axios.get(url, requestConfig);
      const list = Array.isArray(response) ? response : [response];
      setProperties(list);
      if (list.length > 0) {
        setReconcilePropertyId(list[0].id);
      }
    } catch (e) { /* ignore */ }
  };

  const loadTenantsForProperty = async (propertyId) => {
    try {
      const unitsRes = await axios.get(
        `${process.env.REACT_APP_API_URL}units?where={"property_id":"${propertyId}"}&take=10000`,
        requestConfig,
      );
      const unitList = unitsRes.data.response || [];
      setUnits(unitList);

      const unitIds = unitList.map((u) => u.id).join(',');
      if (!unitIds) { setTenants([]); return; }
      const tenantsRes = await axios.get(
        `${process.env.REACT_APP_API_URL}tenants?where={"unit_id":"in::${unitIds}","status":"active"}&take=10000`,
        requestConfig,
      );
      const tenantList = tenantsRes.data.response || [];
      setTenants(tenantList);
      if (tenantList.length > 0) {
        setReconcileTenantId(tenantList[0].id);
        setReconcileUnitId(tenantList[0].unit_id);
      }
    } catch (e) { /* ignore */ }
  };

  const loadRecords = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}suspense?take=1000&order={"createdat":"DESC"}`;
      const { data: { response } } = await axios.get(url, requestConfig);
      setAllRecords(Array.isArray(response) ? response : (response ? [response] : []));
    } catch (e) {
      IziToast('Failed to load suspense records');
    }
  };

  const clearFilters = () => {
    setFilterDate('');
    setFilterStatus('');
    setFilterParticular('');
    setFilterPlatform('');
  };

  // ── Helpers used by the exports below ─────────────────────────────────
  const statusLabel = (s) => (s === 'reconciled' ? 'Reconciled' : 'Pending');

  // Map stored particular slug → display label (mirrors PaymentRedirection).
  const particularLabel = (p) => {
    switch (p) {
      case 'rent': return 'Rent';
      case 'water': return 'Water';
      case 'service_charge': return 'Service Charge';
      case 'utilities': return 'Utilities';
      case 'charges': return 'Charges';
      case 'other': return 'Other';
      default: return p || '-';
    }
  };

  // Map raw payment-platform code → { label, bg, fg } for the Platform pill.
  // The raw codes come straight off `suspense_payments.channel` (mpesa, ncba,
  // co-op, equity, kcb, …). Unknown codes get a neutral grey pill so new
  // gateways don't break the UI. Declared up here (rather than near the JSX)
  // because the `platformOptions` useMemo below needs it.
  const channelInfo = (c) => {
    const code = (c || '').toLowerCase().trim();
    switch (code) {
      case 'mpesa':
      case 'm-pesa':
        return { label: 'M-Pesa', bg: '#e6f4ea', fg: '#1e7e34' };
      case 'ncba':
        return { label: 'NCBA', bg: '#e7eefb', fg: '#1f3c88' };
      case 'co-op':
      case 'coop':
      case 'cooperative':
        return { label: 'Co-op', bg: '#fdecea', fg: '#a4282d' };
      case 'equity':
        return { label: 'Equity', bg: '#fdecea', fg: '#a4282d' };
      case 'kcb':
        return { label: 'KCB', bg: '#fff7e0', fg: '#8a6d1b' };
      case 'cash':
        return { label: 'Cash', bg: '#eef2f5', fg: '#3b4a59' };
      case 'bank':
        return { label: 'Bank', bg: '#eef2f5', fg: '#3b4a59' };
      default:
        return { label: c ? c.toUpperCase() : '—', bg: '#f1f3f5', fg: '#5b6b7c' };
    }
  };

  // ── CSV export of the currently visible (filtered) rows ───────────────
  const exportCSV = () => {
    if (records.length === 0) {
      IziToast('No records to export');
      return;
    }
    const headers = [
      'No.', 'Transaction Time', 'Amount', 'Platform', 'Tx Code',
      'Payer Name', 'Payer Phone', 'Narration', 'Status',
      'Settled To', 'Settled Unit', 'Time Settled',
      'Particular', 'Reconciled By', 'Reason',
    ];
    const escape = (val) => {
      if (val === null || val === undefined) return '';
      const s = String(val).replace(/"/g, '""');
      return /[",\n\r]/.test(s) ? `"${s}"` : s;
    };
    const rows = records.map((r, idx) => [
      idx + 1,
      moment(r.createdat).format('YYYY-MM-DD HH:mm'),
      `${currency} ${(r.amount || 0).toLocaleString(undefined, moneyOpts)}`,
      channelInfo(r.channel).label,
      r.pay_code || '',
      r.pay_payer_name || '',
      r.pay_phone || '',
      r.pay_narration || '',
      statusLabel(r.status),
      r.settled_to_tenant_name || '',
      r.settled_to_tenant_unit || '',
      r.reconciled_at ? moment(r.reconciled_at).format('YYYY-MM-DD HH:mm') : '',
      r.particular ? particularLabel(r.particular) : '',
      r.reconciled_by_name || '',
      r.reason || '',
    ].map(escape).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    // BOM for Excel UTF-8 compatibility
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `suspense-account-${moment().format('YYYY-MM-DD-HHmm')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ── PDF export via the browser print dialog ───────────────────────────
  const exportPDF = () => {
    if (records.length === 0) {
      IziToast('No records to export');
      return;
    }
    const win = window.open('', '_blank', 'width=1000,height=700');
    if (!win) {
      IziToast('Please allow popups to export PDF');
      return;
    }
    const filterSummary = [
      filterDate ? `Date: ${filterDate}` : null,
      filterStatus ? `Status: ${statusLabel(filterStatus)}` : null,
      filterParticular ? `Particular: ${particularLabel(filterParticular)}` : null,
      filterPlatform ? `Platform: ${channelInfo(filterPlatform).label}` : null,
    ].filter(Boolean).join(' &nbsp;|&nbsp; ') || 'All records';

    const pendingTotalStr = `${currency} ${total.toLocaleString(undefined, moneyOpts)}`;

    const escapeHtml = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const rowsHtml = records.map((r, idx) => `
      <tr>
        <td>${idx + 1}</td>
        <td>${moment(r.createdat).format('D/M/YYYY HH:mm')}</td>
        <td>${currency} ${(r.amount || 0).toLocaleString(undefined, moneyOpts)}</td>
        <td>${escapeHtml(channelInfo(r.channel).label)}</td>
        <td>
          ${r.pay_code ? `<code>${escapeHtml(r.pay_code)}</code><br/>` : ''}
          ${r.pay_payer_name ? `<strong>${escapeHtml(r.pay_payer_name)}</strong><br/>` : ''}
          ${r.pay_phone ? `<small>${escapeHtml(r.pay_phone)}</small>` : ''}
        </td>
        <td>${statusLabel(r.status)}</td>
        <td>
          ${escapeHtml(r.settled_to_tenant_name || '-')}
          ${r.settled_to_tenant_unit ? `<br/><small>Unit ${escapeHtml(r.settled_to_tenant_unit)}</small>` : ''}
        </td>
        <td>${r.reconciled_at ? moment(r.reconciled_at).format('D/M/YYYY HH:mm') : '-'}</td>
        <td>${r.particular ? escapeHtml(particularLabel(r.particular)) : '-'}</td>
        <td>${escapeHtml(r.reconciled_by_name || '-')}</td>
        <td>${escapeHtml(r.reason || '-')}</td>
      </tr>
    `).join('');

    win.document.write(`
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Suspense Account Log</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; color: #333; }
          h2 { color: #c0392b; margin: 0 0 4px; }
          .meta { font-size: 12px; color: #777; margin-bottom: 14px; }
          .total { font-size: 13px; color: #c0392b; font-weight: bold; margin-bottom: 18px; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th { background: #c0392b; color: #fff; padding: 6px; text-align: left; }
          td { padding: 6px; border-bottom: 1px solid #eee; vertical-align: top; }
          tr:nth-child(even) td { background: #fafafa; }
          code { background: #f5f5f5; padding: 1px 4px; border-radius: 2px; font-size: 11px; }
          small { color: #888; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h2>Suspense Account Log</h2>
        <div class="meta">
          Exported ${moment().format('D/M/YYYY HH:mm')} &nbsp;|&nbsp;
          ${records.length} record(s) &nbsp;|&nbsp;
          ${filterSummary}
        </div>
        <div class="total">Pending Total (current view): ${pendingTotalStr}</div>
        <table>
          <thead>
            <tr>
              <th>No.</th><th>Time</th><th>Amount</th><th>Platform</th>
              <th>Transaction Details</th><th>Status</th>
              <th>Settled To</th><th>Time Settled</th>
              <th>Particular</th><th>Reconciled By</th><th>Reason</th>
            </tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>
        <script>
          window.onload = function() {
            window.print();
            setTimeout(function(){ window.close(); }, 500);
          };
        </script>
      </body>
      </html>
    `);
    win.document.close();
  };

  // ── Live-filtered view (no "Search" round-trip) ──────────────────────────
  // Status / Particular / Date / Platform all apply on the client over the
  // loaded set. Date matches by calendar day of the record's createdat.
  // Platform compares the normalised lower-cased channel code so 'MPESA',
  // 'mpesa', and 'M-Pesa' all bucket together.
  const records = useMemo(() => {
    return (allRecords || []).filter((r) => {
      if (filterStatus && r.status !== filterStatus) return false;
      if (filterParticular && r.particular !== filterParticular) return false;
      if (filterPlatform) {
        const rPlat = (r.channel || '').toLowerCase().trim();
        if (rPlat !== filterPlatform) return false;
      }
      if (filterDate) {
        const recordDay = moment(r.createdat).format('YYYY-MM-DD');
        if (recordDay !== filterDate) return false;
      }
      return true;
    });
  }, [allRecords, filterDate, filterStatus, filterParticular, filterPlatform]);

  // Unique platforms present in the loaded set, sorted alphabetically.
  // We derive from data so the dropdown only shows platforms that actually
  // have records — keeps the UI tidy as new gateways are added.
  const platformOptions = useMemo(() => {
    const seen = new Map(); // code → label
    (allRecords || []).forEach((r) => {
      const code = (r.channel || '').toLowerCase().trim();
      if (code && !seen.has(code)) seen.set(code, channelInfo(r.channel).label);
    });
    return Array.from(seen.entries())
      .map(([code, label]) => ({ code, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allRecords]);

  // Pending total reflects the CURRENT filtered view — admins filtering by
  // "pending" + "water" see the unsettled water total at a glance.
  const total = useMemo(() => (
    records
      .filter((r) => r.status !== 'reconciled')
      .reduce((sum, r) => sum + (Number(r.amount) || 0), 0)
  ), [records]);

  // Re-initialise the jQuery DataTable whenever the visible list changes
  // (initial load OR any filter change). Destroying first restores the table
  // to plain DOM so React's row update doesn't hit insertBefore/removeChild
  // crashes (same pattern as ChargesTable / Properties).
  useEffect(() => {
    DestroyTable('suspenseTable');
    const t = setTimeout(() => DrawTable('suspenseTable'), 100);
    return () => clearTimeout(t);
  }, [records]);

  const openReconcileModal = (record) => {
    setSelectedRecord(record);
    setReconcileReason('');
    setReconcileParticular('rent');
    if (properties.length > 0) setReconcilePropertyId(properties[0].id);
    $('#reconcileModal').modal('show');
  };

  const handleReconcile = async () => {
    if (!reconcileTenantId || !reconcileReason) {
      IziToast('Please select a tenant and enter a reason');
      return;
    }
    setIsReconciling(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}suspense/reconcile`,
        {
          suspense_id: selectedRecord.id,
          tenant_id: reconcileTenantId,
          unit_id: reconcileUnitId,
          particular: reconcileParticular,
          reason: reconcileReason,
        },
        requestConfig,
      );
      IziToast('Payment successfully reconciled');
      $('#reconcileModal').modal('hide');
      loadRecords();
    } catch (e) {
      IziToast(e.response?.data?.error || 'Failed to reconcile payment');
    }
    setIsReconciling(false);
  };

  const currency = 'KES';

  return (
    <Master redirectLink="suspenseaccount" title="Finance" source="Suspense Account">
      {/* ── Toolbar: filters · actions · pending total ─────────────────── */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #ececec',
          borderRadius: 8,
          padding: 14,
          marginBottom: 16,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          gap: 12,
        }}
      >
        <div style={{ flex: '0 0 170px' }}>
          <label style={{ fontSize: 12, color: '#7c8896', marginBottom: 4, display: 'block' }}>
            Payment Date
          </label>
          <input
            type="date"
            className="form-control"
            style={{ height: 36, borderColor: '#e2e5e9', boxShadow: 'none' }}
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
          />
        </div>
        <div style={{ flex: '0 0 150px' }}>
          <label style={{ fontSize: 12, color: '#7c8896', marginBottom: 4, display: 'block' }}>
            Status
          </label>
          <select
            className="form-control"
            style={{ height: 36, borderColor: '#e2e5e9', boxShadow: 'none' }}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="reconciled">Reconciled</option>
          </select>
        </div>
        <div style={{ flex: '0 0 170px' }}>
          <label style={{ fontSize: 12, color: '#7c8896', marginBottom: 4, display: 'block' }}>
            Particular
          </label>
          <select
            className="form-control"
            style={{ height: 36, borderColor: '#e2e5e9', boxShadow: 'none' }}
            value={filterParticular}
            onChange={(e) => setFilterParticular(e.target.value)}
          >
            <option value="">All</option>
            <option value="rent">Rent</option>
            <option value="water">Water</option>
            <option value="service_charge">Service Charge</option>
            <option value="utilities">Utilities</option>
            <option value="charges">Charges</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div style={{ flex: '0 0 150px' }}>
          <label style={{ fontSize: 12, color: '#7c8896', marginBottom: 4, display: 'block' }}>
            Platform
          </label>
          <select
            className="form-control"
            style={{ height: 36, borderColor: '#e2e5e9', boxShadow: 'none' }}
            value={filterPlatform}
            onChange={(e) => setFilterPlatform(e.target.value)}
          >
            <option value="">All</option>
            {platformOptions.map((p) => (
              <option key={p.code} value={p.code}>{p.label}</option>
            ))}
          </select>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-default btn-sm"
            onClick={clearFilters}
            title="Clear all filters"
          >
            <i className="fa fa-times" style={{ marginRight: 4 }} />
            Clear
          </button>
          <button
            type="button"
            className="btn btn-default btn-sm"
            onClick={loadRecords}
            title="Re-fetch from the server"
          >
            <i className="fa fa-refresh" style={{ marginRight: 4 }} />
            Refresh
          </button>
          <button
            type="button"
            className="btn btn-success btn-sm"
            onClick={exportCSV}
            title="Download filtered rows as CSV"
          >
            <i className="fa fa-file-text-o" style={{ marginRight: 4 }} />
            CSV
          </button>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={exportPDF}
            title="Open print dialog (Save as PDF)"
          >
            <i className="fa fa-file-pdf-o" style={{ marginRight: 4 }} />
            PDF
          </button>
        </div>

        {/* Pending total — softer card pushed to the right */}
        <div
          style={{
            marginLeft: 'auto',
            border: '1px solid #ececec',
            borderRadius: 6,
            padding: '8px 14px',
            background: '#fafafa',
            minWidth: 200,
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: '#7c8896',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Pending Total
            {(filterDate || filterStatus || filterParticular || filterPlatform) && (
              <span style={{ color: '#aab3bd', textTransform: 'none', letterSpacing: 0 }}>
                {' '}— filtered
              </span>
            )}
          </div>
          <div style={{ color: 'var(--red_color)', fontSize: 20, fontWeight: 700, marginTop: 2 }}>
            {currency} {total.toLocaleString(undefined, moneyOpts)}
          </div>
          <div style={{ color: '#aab3bd', fontSize: 11, marginTop: 2 }}>
            Showing <strong style={{ color: '#5b6b7c' }}>{records.length}</strong>
            {' '}of {allRecords.length}
          </div>
        </div>
      </div>

      {/* Table */}
      <h4 style={{
        color: '#2c3e50',
        fontSize: 15,
        fontWeight: 600,
        margin: '0 0 10px',
      }}
      >
        Suspense Account Log
      </h4>
      <div className="table-responsive" style={{
        background: '#fff',
        border: '1px solid #ececec',
        borderRadius: 8,
      }}
      >
        <table id="suspenseTable" className="table table-striped" style={{ marginBottom: 0 }}>
          <thead style={{ background: '#f5f6f7' }}>
            <tr style={{ color: '#5b6b7c', fontSize: 12 }}>
              <th>No.</th>
              <th>Transaction Time</th>
              <th>Amount</th>
              <th>Platform</th>
              <th>Transaction Details</th>
              <th>Status</th>
              <th>Settled to</th>
              <th>Time Settled</th>
              <th>Particular</th>
              <th>Reconciled by</th>
              <th>Reason</th>
              <th style={{ textAlign: 'center' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {records.map((record, idx) => (
              <tr key={record.id}>
                <td>{idx + 1}</td>
                <td>{moment(record.createdat).format('D/M/YYYY - HH:mm')}</td>
                <td>{currency} {(record.amount || 0).toLocaleString(undefined, moneyOpts)}</td>
                <td>
                  {(() => {
                    const info = channelInfo(record.channel);
                    return (
                      <span style={{
                        display: 'inline-block',
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '0.28em 0.6em',
                        borderRadius: 10,
                        background: info.bg,
                        color: info.fg,
                      }}
                      >
                        {info.label}
                      </span>
                    );
                  })()}
                </td>
                <td style={{ fontSize: 12, lineHeight: '1.5' }}>
                  {record.pay_code && (
                    <div>
                      <span style={{ color: '#666' }}>Tx:</span>{' '}
                      <code style={{ background: '#f5f5f5', padding: '1px 4px', borderRadius: 2 }}>
                        {record.pay_code}
                      </code>
                    </div>
                  )}
                  {record.pay_payer_name && (
                    <div>
                      <span style={{ color: '#666' }}>From:</span>{' '}
                      <strong>{record.pay_payer_name}</strong>
                    </div>
                  )}
                  {record.pay_phone && (
                    <div>
                      <span style={{ color: '#666' }}>Phone:</span>{' '}
                      {record.pay_phone}
                    </div>
                  )}
                  {!record.pay_payer_name && record.pay_account && (
                    <div>
                      <span style={{ color: '#666' }}>Acct:</span>{' '}
                      {record.pay_account}
                    </div>
                  )}
                  {record.pay_narration && (
                    <div
                      style={{
                        color: '#999',
                        fontSize: 11,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {record.pay_narration}
                    </div>
                  )}
                  {!record.pay_code && !record.pay_payer_name && !record.pay_phone
                    && !record.pay_account && !record.pay_narration && '-'}
                </td>
                <td>
                  {record.status === 'reconciled' ? (
                    <span style={{
                      display: 'inline-block',
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '0.28em 0.6em',
                      borderRadius: 10,
                      background: '#e8f6ee',
                      color: '#2e7d4f',
                    }}
                    >
                      Reconciled
                    </span>
                  ) : (
                    <span style={{
                      display: 'inline-block',
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '0.28em 0.6em',
                      borderRadius: 10,
                      background: '#fdf3e3',
                      color: '#b9831e',
                    }}
                    >
                      Pending
                    </span>
                  )}
                </td>
                <td>
                  {record.settled_to_tenant_name ? (
                    <span>
                      {record.settled_to_tenant_name}
                      {record.settled_to_tenant_unit && (
                        <small style={{ color: '#888', display: 'block' }}>
                          Unit {record.settled_to_tenant_unit}
                        </small>
                      )}
                    </span>
                  ) : (
                    <span style={{ color: '#bbb' }}>—</span>
                  )}
                </td>
                <td>
                  {record.reconciled_at
                    ? moment(record.reconciled_at).format('D/M/YYYY - HH:mm')
                    : <span style={{ color: '#bbb' }}>—</span>}
                </td>
                <td>
                  {record.particular
                    ? particularLabel(record.particular)
                    : <span style={{ color: '#bbb' }}>—</span>}
                </td>
                <td>{record.reconciled_by_name || <span style={{ color: '#bbb' }}>—</span>}</td>
                <td>{record.reason || <span style={{ color: '#bbb' }}>—</span>}</td>
                <td style={{ textAlign: 'center' }}>
                  {record.status === 'pending' ? (
                    <button
                      type="button"
                      onClick={() => openReconcileModal(record)}
                      style={{
                        border: '1px solid var(--red_color)',
                        background: 'var(--red_color)',
                        color: '#fff',
                        padding: '4px 12px',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'filter 0.15s ease',
                      }}
                      onMouseOver={(e) => { e.currentTarget.style.filter = 'brightness(0.9)'; }}
                      onFocus={(e) => { e.currentTarget.style.filter = 'brightness(0.9)'; }}
                      onMouseOut={(e) => { e.currentTarget.style.filter = 'none'; }}
                      onBlur={(e) => { e.currentTarget.style.filter = 'none'; }}
                    >
                      Reconcile
                    </button>
                  ) : (
                    <span style={{ color: '#aab3bd', fontSize: 12 }}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Reconcile Modal */}
      <div className="modal fade" id="reconcileModal" tabIndex="-1" role="dialog">
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <button type="button" className="close" data-dismiss="modal">
                <span>&times;</span>
              </button>
              <h4 className="modal-title">Reconcile Payment</h4>
            </div>
            <div className="modal-body">
              {selectedRecord && (
                <div>
                  <p>
                    <strong>Amount:</strong> {currency} {(selectedRecord.amount || 0).toLocaleString(undefined, moneyOpts)}&nbsp;&nbsp;
                    <strong>Code:</strong> {selectedRecord.pay_code || '-'}&nbsp;&nbsp;
                    <strong>Platform:</strong> {channelInfo(selectedRecord.channel).label}
                  </p>
                  <hr />
                  <div className="row">
                    <div className="col-md-12">
                      <label className="control-label">Select Property</label>
                      <select
                        className="form-control"
                        value={reconcilePropertyId}
                        onChange={(e) => setReconcilePropertyId(e.target.value)}
                      >
                        {properties.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <br />
                  <div className="row">
                    <div className="col-md-12">
                      <label className="control-label">Select Unit / Tenant</label>
                      <select
                        className="form-control"
                        value={reconcileTenantId}
                        onChange={(e) => {
                          setReconcileTenantId(e.target.value);
                          const t = tenants.find((x) => x.id === e.target.value);
                          if (t) setReconcileUnitId(t.unit_id);
                        }}
                      >
                        <option value="">-- Select tenant --</option>
                        {tenants.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.first_name} {t.last_name} —{' '}
                            {t.other_details && t.other_details.unit_number
                              ? `Unit ${t.other_details.unit_number}` : t.unit_id}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <br />
                  <div className="row">
                    <div className="col-md-12">
                      <label className="control-label">Particular</label>
                      <select
                        className="form-control"
                        value={reconcileParticular}
                        onChange={(e) => setReconcileParticular(e.target.value)}
                      >
                        <option value="rent">Rent</option>
                        <option value="water">Water</option>
                        <option value="service_charge">Service Charge</option>
                        <option value="utilities">Utilities</option>
                        <option value="charges">Charges</option>
                        <option value="other">Other</option>
                      </select>
                      <small style={{ color: '#888', display: 'block', marginTop: 4 }}>
                        The chosen particular drives which invoices the payment
                        settles first; any leftover then clears other arrears
                        (overpayment rule).
                      </small>
                    </div>
                  </div>
                  <br />
                  <div className="row">
                    <div className="col-md-12">
                      <label className="control-label">Reason for Reconciliation</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Wrong house number, used different phone"
                        value={reconcileReason}
                        onChange={(e) => setReconcileReason(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* ── Sanity-check summary ────────────────────────────── */}
                  {(() => {
                    const prop = properties.find((p) => p.id === reconcilePropertyId);
                    const tnt = tenants.find((t) => t.id === reconcileTenantId);
                    const tntName = tnt
                      ? `${tnt.first_name || ''} ${tnt.last_name || ''}`.trim() : null;
                    const tntUnit = tnt && tnt.other_details
                      ? tnt.other_details.unit_number : null;
                    return (
                      <div
                        style={{
                          marginTop: 18,
                          background: tnt ? '#f1f8f3' : '#fff8e1',
                          border: `1px solid ${tnt ? '#cfe5d4' : '#ffe082'}`,
                          borderRadius: 4,
                          padding: '12px 14px',
                          fontSize: 13,
                        }}
                      >
                        <div style={{
                          fontWeight: 'bold',
                          color: '#2c3e50',
                          marginBottom: 6,
                        }}
                        >
                          <i
                            className={`fa ${tnt ? 'fa-check-circle' : 'fa-info-circle'}`}
                            style={{ marginRight: 6, color: tnt ? '#27ae60' : '#f39c12' }}
                          />
                          You are about to reconcile
                        </div>
                        {tnt ? (
                          <div style={{ color: '#444', lineHeight: '1.6' }}>
                            {currency} <strong>{(selectedRecord.amount || 0).toLocaleString(undefined, moneyOpts)}</strong>
                            {' '}from suspense
                            {selectedRecord.pay_code && (
                              <>
                                {' ('}<code style={{
                                  background: '#fff',
                                  padding: '0 4px',
                                  border: '1px solid #ddd',
                                  borderRadius: 2,
                                }}
                                >{selectedRecord.pay_code}</code>{')'}
                              </>
                            )}
                            {' → '}
                            <strong>{tntName || '—'}</strong>
                            {tntUnit && (
                              <span style={{ color: '#5b6b7c' }}>
                                {' '}(Unit {tntUnit})
                              </span>
                            )}
                            <br />
                            <span style={{ color: '#777' }}>Property:</span>{' '}
                            <strong>{(prop && prop.name) || '—'}</strong>
                            <span style={{ marginLeft: 12, color: '#777' }}>Particular:</span>{' '}
                            <strong>{particularLabel(reconcileParticular)}</strong>
                            {tnt.phone && (
                              <>
                                <span style={{ marginLeft: 12, color: '#777' }}>Phone:</span>{' '}
                                {tnt.phone}
                              </>
                            )}
                          </div>
                        ) : (
                          <div style={{ color: '#7a5d00' }}>
                            Pick a tenant above to see the reconciliation summary.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-default" data-dismiss="modal">Cancel</button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleReconcile}
                disabled={isReconciling}
              >
                {isReconciling ? 'Reconciling...' : 'Reconcile Payment'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Master>
  );
};

const mapStateToProps = (state) => state;
SuspenseAccount.propTypes = { user: PropTypes.oneOfType([PropTypes.object, PropTypes.string]) };
SuspenseAccount.defaultProps = { user: {} };
export default connect(mapStateToProps)(SuspenseAccount);
